import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { supabase } from '../../../lib/supabase';
import { X, CreditCard, Lock, AlertCircle, CheckCircle } from 'lucide-react';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// Inner form component (must be inside Elements)
const StripePaymentForm = ({ onSuccess, onError, isProcessing, setIsProcessing, bookingDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setFormError('');

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `https://phangan.taxi/smart-booking-form`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setFormError(error?.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm on backend
        try {
          await supabase?.functions?.invoke('confirm-payment', {
            body: { paymentIntentId: paymentIntent?.id },
          });
        } catch (confirmErr) {
          console.error('Backend confirmation error:', confirmErr);
        }
        onSuccess(paymentIntent);
      } else {
        setFormError('Payment was not completed. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      setFormError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {formError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600">{formError}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className={`w-full py-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
          !stripe || isProcessing
            ? 'bg-gray-400 cursor-not-allowed' :'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
        }`}
      >
        {isProcessing ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <Lock className="w-4 h-4" />
            <span>Pay {bookingDetails?.amount} THB</span>
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Lock className="w-3 h-3" />
        <span>Secured by Stripe · SSL encrypted</span>
      </div>
    </form>
  );
};

// Main PaymentModal component
const PaymentModal = ({ isOpen, onClose, bookingDetails, onPaymentSuccess }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [initError, setInitError] = useState('');
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');

  // Initialize payment intent when modal opens
  React.useEffect(() => {
    if (isOpen && bookingDetails?.amount && !clientSecret) {
      initializePayment();
    }
    if (!isOpen) {
      // Reset state when modal closes
      setClientSecret('');
      setPaymentIntentId('');
      setInitError('');
      setPaymentSucceeded(false);
      setReceiptUrl('');
    }
  }, [isOpen, bookingDetails?.amount]);

  const initializePayment = async () => {
    setIsLoading(true);
    setInitError('');

    try {
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount: bookingDetails?.amount,
            currency: 'thb',
            description: `Phangan Taxi: ${bookingDetails?.from || ''} → ${bookingDetails?.to || ''}`,
            orderId: bookingDetails?.orderId || null,
            fromZone: bookingDetails?.fromZone || '',
            toZone: bookingDetails?.toZone || '',
            serviceType: bookingDetails?.serviceType || '',
            metadata: {
              pickupLocation: bookingDetails?.from || '',
              destination: bookingDetails?.to || '',
              serviceType: bookingDetails?.serviceType || '',
            },
          },
          customerInfo: {
            userId: bookingDetails?.userId || null,
            email: bookingDetails?.customerEmail || null,
            name: bookingDetails?.customerName || null,
            stripeCustomerId: bookingDetails?.stripeCustomerId || null,
          },
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to initialize payment');
      }

      setClientSecret(data?.clientSecret);
      setPaymentIntentId(data?.paymentIntentId);
    } catch (err) {
      setInitError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setIsProcessing(false);
    setPaymentSucceeded(true);

    // Try to get receipt URL
    try {
      const { data } = await supabase?.functions?.invoke('confirm-payment', {
        body: { paymentIntentId: paymentIntent?.id },
      });
      if (data?.receiptUrl) {
        setReceiptUrl(data?.receiptUrl);
      }
    } catch (e) {
      console.error('Error getting receipt:', e);
    }

    if (onPaymentSuccess) {
      onPaymentSuccess({
        paymentIntentId: paymentIntent?.id,
        amount: bookingDetails?.amount,
        receiptUrl,
      });
    }
  };

  if (!isOpen) return null;

  const stripeOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#2563eb',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
          },
        },
      }
    : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Card Payment</h2>
              <p className="text-sm text-gray-500">Secure payment via Stripe</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Booking Summary */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
            {bookingDetails?.from && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">From:</span>
                <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{bookingDetails?.from}</span>
              </div>
            )}
            {bookingDetails?.to && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">To:</span>
                <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{bookingDetails?.to}</span>
              </div>
            )}
            {bookingDetails?.serviceType && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Service:</span>
                <span className="font-medium text-gray-900">{bookingDetails?.serviceType}</span>
              </div>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-900">Total:</span>
              <span className="font-bold text-blue-600 text-base">{bookingDetails?.amount} THB</span>
            </div>
          </div>

          {/* Payment Succeeded */}
          {paymentSucceeded ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-6">Your booking has been confirmed and payment received.</p>
              {receiptUrl && (
                <a
                  href={receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-4"
                >
                  View Receipt →
                </a>
              )}
              <button
                onClick={onClose}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Done
              </button>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-600 text-sm">Initializing secure payment...</p>
            </div>
          ) : initError ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-700">Payment initialization failed</p>
                  <p className="text-sm text-red-600 mt-1">{initError}</p>
                </div>
              </div>
              <button
                onClick={initializePayment}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : clientSecret && stripeOptions ? (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <StripePaymentForm
                onSuccess={handlePaymentSuccess}
                onError={(err) => setInitError(err)}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                bookingDetails={bookingDetails}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
